import React, { useEffect, useState } from 'react';
import { IonPage, IonContent } from '@ionic/react';
import { 
  Container, 
  Typography, 
  Button, 
  Box,
  Alert
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { auth } from '../firebase/config';
import { sendEmailVerification } from 'firebase/auth';
import LoadingSpinner from '../components/LoadingSpinner';

const EmailVerification: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    // Check if email is verified every 5 seconds
    const interval = setInterval(async () => {
      if (auth.currentUser) {
        try {
          await auth.currentUser.reload();
          if (auth.currentUser.emailVerified) {
            // Force a page reload to update auth state
            window.location.href = '/home';
          }
        } catch (error) {
          console.error('Error checking email verification:', error);
        }
      }
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Countdown timer for resend button
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setResendDisabled(false);
    }
  }, [countdown]);

  const handleResendVerification = async () => {
    if (!auth.currentUser) return;

    setLoading(true);
    setError(null);
    try {
      await sendEmailVerification(auth.currentUser);
      setResendDisabled(true);
      setCountdown(60); // Disable resend for 60 seconds
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner fullScreen message={t('auth.loading')} />;
  }

  return (
    <IonPage>
      <IonContent>
        <Container maxWidth="sm">
          <Box
            sx={{
              mt: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: 3
            }}
          >
            <Typography variant="h4" component="h1" gutterBottom>
              {t('auth.verifyEmail')}
            </Typography>

            <Typography variant="body1" color="text.secondary">
              {t('auth.verificationEmailSent', { email: auth.currentUser?.email })}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ width: '100%' }}>
                {error}
              </Alert>
            )}

            <Button
              variant="contained"
              onClick={handleResendVerification}
              disabled={resendDisabled}
              sx={{ mt: 2 }}
            >
              {resendDisabled 
                ? t('auth.resendIn', { seconds: countdown })
                : t('auth.resendVerification')
              }
            </Button>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {t('auth.checkSpam')}
            </Typography>
          </Box>
        </Container>
      </IonContent>
    </IonPage>
  );
};

export default EmailVerification;
